/**
 * NietBeschikbaarDialog — Shows all reservations for a material on a calendar
 * Uses identical FullCalendar styling as DraaidagenOverzicht maandoverzicht
 * Conflicts (overlapping with your period) are red, others are grey
 * Stitch design system
 */
import React, { useMemo } from 'react';
import { Box, Dialog, DialogContent, DialogActions, Typography, Tooltip } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import nlLocale from '@fullcalendar/core/locales/nl';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  error: '#a83836', errorBg: '#fef2f2',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const fmtDate = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(typeof d === 'string' && !d.includes('T') ? d + 'T12:00:00' : d);
  if (isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'long' });
};

const toDateStr = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(typeof d === 'string' && !d.includes('T') ? d + 'T12:00:00' : d);
  if (isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
};

const addDays = (dateStr, n) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const NietBeschikbaarDialog = ({ open, onClose, reservations, materialName, requestedStart, requestedEnd, extraDagVoor, extraDagNa, statusReason = null }) => {
  const reqStartStr = toDateStr(requestedStart);
  const reqEndStr = toDateStr(requestedEnd) || reqStartStr;
  const effStartStr = extraDagVoor && reqStartStr ? addDays(reqStartStr, -1) : reqStartStr;
  const effEndStr = extraDagNa && reqEndStr ? addDays(reqEndStr, 1) : reqEndStr;

  // Determine which reservations are conflicts (overlap with effective period)
  const isConflict = (res) => {
    if (!effStartStr || !effEndStr) return false;
    const effStart = new Date(effStartStr + 'T00:00:00');
    const effEnd = new Date(effEndStr + 'T23:59:59');
    const resStart = res.startDatum instanceof Date ? res.startDatum : new Date(res.startDatum);
    const resEnd = (res.eindDatum instanceof Date ? res.eindDatum : new Date(res.eindDatum || res.startDatum));
    return resStart <= effEnd && resEnd >= effStart;
  };

  // conflictCount is calculated after deduplication below

  // Deduplicate reservations — group by draaidagNaam to merge per-day entries, preserve extra dag flags
  const deduplicatedReservations = useMemo(() => {
    const byKey = {};
    (reservations || []).forEach(res => {
      const key = res.draaidagNaam || `res-${Math.random()}`;
      const s = res.startDatum instanceof Date ? res.startDatum : new Date(res.startDatum);
      const e = res.eindDatum instanceof Date ? res.eindDatum : new Date(res.eindDatum || res.startDatum);
      if (!byKey[key]) {
        byKey[key] = { draaidagNaam: res.draaidagNaam, startDatum: s, eindDatum: e, extraDagVoor: res.extraDagVoor || false, extraDagNa: res.extraDagNa || false, draaidagStart: res.draaidagStart, draaidagEnd: res.draaidagEnd };
      } else {
        if (s < byKey[key].startDatum) byKey[key].startDatum = s;
        if (e > byKey[key].eindDatum) byKey[key].eindDatum = e;
        if (res.extraDagVoor) byKey[key].extraDagVoor = true;
        if (res.extraDagNa) byKey[key].extraDagNa = true;
      }
    });
    return Object.values(byKey);
  }, [reservations]);

  const conflictCount2 = useMemo(() => deduplicatedReservations.filter(res => {
    if (!effStartStr || !effEndStr) return false;
    const effStart = new Date(effStartStr + 'T00:00:00');
    const effEnd = new Date(effEndStr + 'T23:59:59');
    return res.startDatum <= effEnd && res.eindDatum >= effStart;
  }).length, [deduplicatedReservations, effStartStr, effEndStr]);

  // Build calendar events
  const calendarEvents = useMemo(() => {
    const events = [];

    deduplicatedReservations.forEach((res, i) => {
      const startStr = toDateStr(res.startDatum);
      const endStr = toDateStr(res.eindDatum) || startStr;
      if (!startStr) return;

      const effStart = effStartStr ? new Date(effStartStr + 'T00:00:00') : null;
      const effEnd = effEndStr ? new Date(effEndStr + 'T23:59:59') : null;
      const conflict = effStart && effEnd && res.startDatum <= effEnd && res.eindDatum >= effStart;
      const bgColor = conflict ? '#ef4444' : '#cbe7df';
      const hasExtra = res.extraDagVoor || res.extraDagNa;

      events.push({
        id: `res-${i}`,
        title: res.draaidagNaam || 'Gereserveerd',
        start: startStr,
        end: addDays(endStr, 1),
        allDay: true,
        backgroundColor: bgColor,
        borderColor: bgColor,
        textColor: conflict ? '#fff' : T.text,
        classNames: hasExtra ? ['has-extra-days'] : [],
        extendedProps: { hasExtra, extraDagVoor: res.extraDagVoor, extraDagNa: res.extraDagNa, draaidagStart: res.draaidagStart, draaidagEnd: res.draaidagEnd },
      });
    });

    // Your draaidag — single event spanning full range
    if (reqStartStr) {
      events.push({
        id: 'own',
        title: 'Jouw draaidag',
        start: effStartStr,
        end: addDays(effEndStr, 1),
        allDay: true,
        backgroundColor: T.primary,
        borderColor: T.primary,
        textColor: '#fff',
        classNames: (extraDagVoor || extraDagNa) ? ['has-extra-days'] : [],
        extendedProps: { isOwn: true, extraDagVoor, extraDagNa },
      });
    }

    return events;
  }, [deduplicatedReservations, reqStartStr, effStartStr, effEndStr, extraDagVoor, extraDagNa]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '0.75rem', overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '0.75rem', bgcolor: T.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="event_busy" size={22} sx={{ color: T.error }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>
              Niet beschikbaar
            </Typography>
            {materialName && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary, mt: 0.25 }}>
                {statusReason ? materialName : `${materialName} — ${conflictCount2} conflict${conflictCount2 !== 1 ? 'en' : ''}`}
              </Typography>
            )}
          </Box>
        </Box>
        <Box onClick={onClose} sx={{ cursor: 'pointer', display: 'flex', p: 0.5, borderRadius: '0.35rem', '&:hover': { bgcolor: T.surfaceLow } }}>
          <Icon name="close" size={20} sx={{ color: T.outline }} />
        </Box>
      </Box>

      <DialogContent sx={{ pt: 0, px: 3 }}>
        {/* Status-reden (reparatie / lange tijd afwezig) i.p.v. datum-conflict */}
        {statusReason && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 1, borderRadius: '0.75rem', bgcolor: T.errorBg, border: `1px solid ${T.error}25` }}>
            <Icon name="build" size={20} sx={{ color: T.error }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>{statusReason}</Typography>
          </Box>
        )}
        {/* Your period info */}
        {!statusReason && reqStartStr && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: '0.75rem', bgcolor: T.primaryContainer, border: `1px solid ${T.primary}25` }}>
            <Icon name="calendar_today" size={18} sx={{ color: T.primary }} />
            <Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.primary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Jouw draaidag
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: T.primaryDim }}>
                {extraDagVoor && <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, mr: 0.5 }}>(-1)</Typography>}
                {fmtDate(requestedStart)}{requestedEnd && reqStartStr !== reqEndStr ? ` – ${fmtDate(requestedEnd)}` : ''}
                {extraDagNa && <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, ml: 0.5 }}>(+1)</Typography>}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Calendar — identical styling to DraaidagenOverzicht */}
        {!statusReason && (<>
        <Box sx={{
          mt: 1,
          // Exact same Stitch FullCalendar styling as DraaidagenOverzicht
          '& .fc': { fontFamily: '"Inter", system-ui, sans-serif', fontSize: '0.8rem' },
          '& .fc-toolbar-title': {
            fontSize: '1.15rem !important', fontWeight: 700,
            fontFamily: '"Manrope", system-ui, sans-serif',
            color: '#2d3335',
          },
          '& .fc-button-primary': {
            backgroundColor: '#fff !important', borderColor: '#e2e8f0 !important',
            color: '#526075 !important', fontSize: '0.75rem !important',
            fontWeight: '600 !important', borderRadius: '8px !important',
            padding: '4px 12px !important', textTransform: 'capitalize !important',
            transition: 'all 0.15s !important',
            '&:hover': { backgroundColor: '#f1f4f5 !important', borderColor: '#526075 !important' },
            '&.fc-button-active': {
              backgroundColor: '#526075 !important', borderColor: '#526075 !important',
              color: '#fff !important',
            },
          },
          '& .fc-button-group': { borderRadius: '8px', overflow: 'hidden' },
          '& .fc-day-today': { backgroundColor: 'rgba(82,96,117,0.04) !important' },
          '& .fc-event': {
            borderRadius: '6px !important', fontSize: '0.7rem !important',
            padding: '2px 6px !important', cursor: 'default',
            lineHeight: '1.45 !important', border: 'none !important',
            fontWeight: '500 !important',
            boxShadow: '0 1px 2px rgba(45,51,53,0.06)',
          },
          '& .fc-daygrid-event-dot': { display: 'none' },
          '& .fc-daygrid-day-frame': { minHeight: 85 },
          '& .fc-col-header-cell-cushion': {
            fontWeight: 700, fontSize: '0.7rem', color: '#556071',
            textTransform: 'lowercase', letterSpacing: '0.02em',
            padding: '8px 0 !important',
          },
          '& .fc-scrollgrid': { borderColor: '#e5e7eb', borderRadius: '8px', overflow: 'hidden' },
          '& .fc-theme-standard td, & .fc-theme-standard th': { borderColor: 'rgba(45,51,53,0.06)' },
          '& .fc-daygrid-more-link': { fontSize: '0.65rem', color: '#635983', fontWeight: 700 },
          '& .fc-daygrid-day-number': {
            fontSize: '0.78rem', fontWeight: 600, padding: '6px 8px',
            color: '#526075',
          },
          '& .fc-day-sat, & .fc-day-sun': {
            backgroundColor: '#f8f9fa !important',
            '& .fc-daygrid-day-number': { color: '#adb3b5' },
          },
          // Own draaidag with +1/-1: transparent wrapper, custom content renders the bar
          '& .has-extra-days': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
            boxShadow: 'none !important',
            padding: '0 !important',
            overflow: 'visible !important',
          },
          '& .has-extra-days .fc-event-main': {
            padding: '0 !important',
          },
        }}>
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            initialDate={reqStartStr || undefined}
            locale={nlLocale}
            headerToolbar={{ left: 'prev,next', center: 'title', right: '' }}
            events={calendarEvents}
            firstDay={1}
            height="auto"
            dayMaxEvents={4}
            moreLinkText={(n) => `+${n} meer`}
            eventContent={(arg) => {
              const props = arg.event.extendedProps;
              const bgColor = arg.event.backgroundColor || T.primary;
              const txtColor = arg.event.textColor || '#fff';
              const hasExtra = props?.extraDagVoor || props?.extraDagNa;

              if (!hasExtra) {
                // Normal event — tooltip + truncated text
                return (
                  <Tooltip title={arg.event.title} arrow enterDelay={300} placement="top">
                    <span style={{
                      display: 'block', width: '100%', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontSize: '0.7rem', lineHeight: '1.45', color: txtColor,
                    }}>
                      {arg.event.title}
                    </span>
                  </Tooltip>
                );
              }

              // Event with +1/-1 — segmented bar identical to DraaidagenOverzicht
              // Calculate total days from event range (which already includes extensions)
              const startD = new Date(arg.event.startStr + 'T12:00:00');
              const endD = new Date(arg.event.endStr + 'T12:00:00');
              const totalDays = Math.max(1, Math.round((endD - startD) / (24 * 60 * 60 * 1000)));

              // Determine extension days — if we have draaidagStart/End, use those to calculate
              // Otherwise fall back to assuming 1 day per extension
              let voorDays = 0, naDays = 0;
              if (props.draaidagStart && props.extraDagVoor) {
                const dsStart = new Date(props.draaidagStart + (props.draaidagStart.includes('T') ? '' : 'T12:00:00'));
                voorDays = Math.max(0, Math.round((dsStart - startD) / (24 * 60 * 60 * 1000)));
              } else if (props.extraDagVoor) {
                voorDays = 1;
              }
              if (props.draaidagEnd && props.extraDagNa) {
                const dsEnd = new Date(props.draaidagEnd + (props.draaidagEnd.includes('T') ? '' : 'T12:00:00'));
                const dsEndPlusOne = new Date(dsEnd.getTime() + 86400000);
                naDays = Math.max(0, Math.round((endD - dsEndPlusOne) / (24 * 60 * 60 * 1000)));
              } else if (props.extraDagNa) {
                naDays = 1;
              }
              const voorPct = (voorDays / totalDays) * 100;
              const naPct = (naDays / totalDays) * 100;

              return (
                <span style={{
                  display: 'flex', width: '100%', height: '100%',
                  lineHeight: '1.45', overflow: 'hidden', borderRadius: '6px',
                }}>
                  {props.extraDagVoor && (
                    <span style={{
                      width: `${voorPct}%`, minWidth: 8, flexShrink: 0,
                      background: `repeating-linear-gradient(115deg, ${bgColor}70, ${bgColor}70 3px, ${bgColor}40 3px, ${bgColor}40 6px)`,
                      border: `1.5px dashed ${bgColor}`,
                      borderRight: 'none',
                      borderRadius: '6px 0 0 6px',
                    }} />
                  )}
                  <span style={{
                    flex: 1, minWidth: 0,
                    backgroundColor: bgColor, color: txtColor,
                    padding: '2px 6px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontSize: '0.7rem', fontWeight: 600,
                    borderRadius: props.extraDagVoor ? 0 : '6px 0 0 6px',
                    borderTopRightRadius: props.extraDagNa ? 0 : 6,
                    borderBottomRightRadius: props.extraDagNa ? 0 : 6,
                  }}>
                    {arg.event.title}
                  </span>
                  {props.extraDagNa && (
                    <span style={{
                      width: `${naPct}%`, minWidth: 8, flexShrink: 0,
                      background: `repeating-linear-gradient(115deg, ${bgColor}70, ${bgColor}70 3px, ${bgColor}40 3px, ${bgColor}40 6px)`,
                      border: `1.5px dashed ${bgColor}`,
                      borderLeft: 'none',
                      borderRadius: '0 6px 6px 0',
                    }} />
                  )}
                </span>
              );
            }}
          />
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 3, mt: 2, mb: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 10, borderRadius: '3px', bgcolor: '#ef4444' }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>Conflict</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 10, borderRadius: '3px', bgcolor: T.primary }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>Jouw draaidag</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 10, borderRadius: '3px', bgcolor: '#cbe7df' }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>Andere reserveringen</Typography>
          </Box>
          {(extraDagVoor || extraDagNa) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 10, borderRadius: '3px', background: `repeating-linear-gradient(115deg, ${T.primary}70, ${T.primary}70 2px, ${T.primary}40 2px, ${T.primary}40 4px)`, border: `1px dashed ${T.primary}` }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
                Extra dag ({extraDagVoor && '-1'}{extraDagVoor && extraDagNa && '/'}{extraDagNa && '+1'})
              </Typography>
            </Box>
          )}
        </Box>
        </>)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Box onClick={onClose} sx={{
          px: 2.5, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
          border: `1.5px solid ${T.outlineVariant}50`,
          fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.secondary,
          transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceLow },
        }}>
          Sluiten
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NietBeschikbaarDialog;
